import { logger } from '@beelday/common'
import { FocusedFeatureTypes, FocusFeature, UUID } from 'common/types'

/**
 * What the participant's phone shows for the feature the trainer has
 * focused. The phone is an answering device, not a second screen: anything
 * that is only watched stays on the projector and the phone rests.
 *
 * `key` identifies one activity. The script moves from one poll to the next
 * without an unfocus in between, so two consecutive activities of the same
 * kind differ only in their id - the view is keyed on it so that nothing
 * typed, picked or sent for the previous one survives into the next.
 */
export type Stage =
	| { kind: 'presenting'; key: string }
	| { kind: 'audio'; key: string }
	| { kind: 'youtube'; key: string }
	| { kind: 'multipleChoice'; key: string }
	| { kind: 'wordCloud'; key: string }
	| { kind: 'openQuestions'; key: string }
	| { kind: 'knowledgeCheck'; key: string; knowledgeCheckId: UUID }
	| { kind: 'groupWork'; key: string }
	| { kind: 'quiz'; key: string }

/** Stages with something to answer - worth interrupting the participant for. */
export const isAnswerable = (stage: Stage): boolean =>
	stage.kind !== 'presenting' &&
	stage.kind !== 'audio' &&
	stage.kind !== 'youtube'

const PRESENTING: Stage = { kind: 'presenting', key: 'presenting' }

export const stageFor = (feature: FocusFeature | undefined): Stage => {
	if (!feature) {
		return PRESENTING
	}

	switch (feature.type) {
		case FocusedFeatureTypes.POLLING_MULTIPLE_CHOICE:
			return { kind: 'multipleChoice', key: feature.pollingId }
		case FocusedFeatureTypes.POLLING_OPEN_ENDED_WORD_CLOUD:
			return { kind: 'wordCloud', key: feature.pollingId }
		case FocusedFeatureTypes.POLLING_OPEN_ENDED_LIST:
			return { kind: 'openQuestions', key: feature.pollingId }
		case FocusedFeatureTypes.KNOWLEDGE_CHECK:
			return {
				kind: 'knowledgeCheck',
				key: feature.knowledgeCheckId,
				knowledgeCheckId: feature.knowledgeCheckId,
			}
		case FocusedFeatureTypes.ICEBREAKER:
			return { kind: 'quiz', key: feature.quizId }
		case FocusedFeatureTypes.SIMPLE_GROUPING:
			return { kind: 'groupWork', key: 'group-work' }
		case FocusedFeatureTypes.AUDIO_STREAMING:
			return { kind: 'audio', key: feature.audioStreamId }
		case FocusedFeatureTypes.YOUTUBE_STREAMING:
			return { kind: 'youtube', key: feature.youtubeId }
		case FocusedFeatureTypes.SLIDE:
		case FocusedFeatureTypes.ScreenShare:
		case FocusedFeatureTypes.WHITEBOARD:
		case FocusedFeatureTypes.NOTEPAD:
		case FocusedFeatureTypes.GROUPING:
			// `Grouping` (breakout rooms) is never enabled in a stationary scheme.
			return PRESENTING
		default: {
			// Compile time: a new focus type must be placed above. Run time: the
			// type comes from the server, so an unknown one rests instead of
			// throwing the participant out of the room.
			const unhandled: never = feature
			logger.warn('Unknown focused feature on the phone', unhandled)
			return PRESENTING
		}
	}
}
